import { WarningOutlined } from '@ant-design/icons';
import { Col, Modal, Row } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import useQuery from '@portal/hooks/useQuery';

import { DetailsAction, TransactionsActions } from '@portal/actions';
import { getRouteStackPath, getStackPath } from '@portal/config/routes';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { translate } from '@portal/services/i18n';

import {
  decreaseDetailsLoading,
  increaseDetailsLoading,
} from '@portal/actions/loading';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import ErrorComponent from '@portal/components/ErrorComponent/ErrorComponent';
import PanelContentBreadcrumb from '@portal/components/PanelContentBreadcrumb/PanelContentBreadcrumb';
import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';
import PaymentStatusIcon from '@portal/components/PaymentStatusIcon/PaymentStatusIcon';
import PixReversalModal from '@portal/components/PixReversalModal/PixReversalModal';
import StatementProofModal from '@portal/components/StatementProofModal/StatementProofModal';
import TransactionDetailsComponent from '@portal/components/TransactionDetails/TransactionDetails';
import TransactionDetailsSkeleton from '@portal/components/TransactionDetails/TransactionDetailsSkeleton';
import { enablePixRefund } from '@portal/config/app.json';
import { TransactionStatus } from '@portal/enum/approvalsType';
import RolesType from '@portal/enum/rolesType';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import {
  StatementProofType,
  TransactionFlow,
  TransactionType,
} from '@portal/models/enumerators';
import { encrypt, signData } from '@portal/repositories/securitySdkConfig';
import { formatMoney } from '@portal/services/masks';
import NavigationService from '@portal/services/navigation';
import { hasAccess } from '@portal/utils/hasAccess';
import * as MessageService from '~/services/message';

const TransactionDetails = () => {
  const [visible, setVisible] = useState(false);
  const [pixReversalModal, setPixReversalModal] = useState(false);
  const [isReversalPinModalVisible, setIsReversalPinModalVisible] =
    useState(false);
  const [isCancelPinModalVisible, setIsCancelPinModalVisible] = useState(false);
  const [reason, setReason] = useState('');
  const [retry, setRetry] = useState<0 | 1 | 2 | 3>(0);

  const [attempts, setAttempts] = useState(0);
  const [isLoadingAttempt, setIsLoadingAttempt] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const params = useParams() as { id: string };
  const dispatch = useDispatch();
  const query = useQuery();
  const {
    user: { roles, me },
    loading: { detailsLoading },
    details: { transaction },
    account: { balance },
  } = useReduxState();

  const [amount, setAmount] = useState(
    transaction?.pixInfo?.availableRefundAmount ?? '0'
  );
  const [rawAmount, setRawAmount] = useState(
    transaction?.pixInfo?.availableRefundAmount ?? 0
  );

  useEffect(() => {
    setAmount(transaction?.pixInfo?.availableRefundAmount ?? '0');
    setRawAmount(transaction?.pixInfo?.availableRefundAmount ?? 0);
  }, [transaction?.pixInfo?.availableRefundAmount]);

  const navigate = useNavigation<{
    isExtract?: boolean;
    isEscrow?: boolean;
    isCashback?: boolean;
    isPayable?: boolean;
    isReceivable?: boolean;
    isScheduled?: boolean;
  }>();

  const getStateValue = (key: string): boolean => {
    if (
      navigate?.location?.state &&
      navigate?.location?.state[key] !== undefined
    ) {
      return Boolean(navigate?.location?.state[key]);
    }

    const queryValue = query.get(key);
    return queryValue === 'true';
  };

  const handleConfirmation = async (pin: string) => {
    if (!transaction?.id || !transaction?.amount || !reason) return;

    if (transaction.pixInfo?.availableRefundAmount) {
      if (rawAmount > transaction.pixInfo.availableRefundAmount) {
        MessageService.error('PIX.AVAILABLE_REFUND_AMOUNT_SURPASSED', {
          interpolation: {
            interpolationField: 'refundAmountValue',
            interpolationValue: `R$ ${formatMoney(
              transaction.pixInfo.availableRefundAmount
            )}`,
          },
        });

        setIsReversalPinModalVisible(false);
        setPixReversalModal(true);
        return;
      }

      if (rawAmount === 0) {
        MessageService.error('PIX.VALUE_INVALID');

        setIsReversalPinModalVisible(false);
        setPixReversalModal(true);
        return;
      }

      if (Number(balance?.available ?? 0) < rawAmount) {
        MessageService.error('INSUFFICIENT_BALANCE');

        setIsReversalPinModalVisible(false);
        setPixReversalModal(true);
        return;
      }

      const encryptedPin = await encrypt(pin);

      dispatch(
        TransactionsActions.pixReversalTransfer({
          amount: rawAmount,
          id: transaction.id,
          pin: encryptedPin,
          reasonCode: reason,
          description: transaction.description,
        })
      );
    }

    setIsReversalPinModalVisible(false);
    setReason('');
  };

  const handleCancelScheduledPix = async (pin: string) => {
    const encryptedPIN = await encrypt(pin);

    if (transaction?.id && transaction?.amount) {
      const timestamp = new Date().getTime().toString();

      const transactionHash = signData(timestamp);

      dispatch(
        TransactionsActions.cancelPixScheduledTransfer(transaction.id, {
          pin: encryptedPIN,
          transactionHash,
          timestamp,
        })
      );
      setIsCancelPinModalVisible(false);
    }
  };

  /**
   * Verifica se transação PIX está em análise antifraude no Decision Desk
   */
  const isInDecisionDeskAnalysis = useMemo(
    () =>
      transaction?.isInDecisionDeskAnalysis &&
      transaction?.type === TransactionType.PIX,
    [transaction?.isInDecisionDeskAnalysis, transaction?.type]
  );

  /**
   * Configuração de título baseada no estado da transação
   *
   * Decision Desk Analysis:
   * Quando uma transação PIX é marcada para análise antifraude pelo Decision Desk,
   * mostramos um ícone de alerta amarelo junto com título e descrição customizados.
   */
  const titleConfig = useMemo(() => {
    if (isInDecisionDeskAnalysis) {
      return {
        title: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE_IN_ANALYSIS'
        ),
        description: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.DESCRIPTION_IN_ANALYSIS'
        ),
        showWarningIcon: true,
      };
    }

    // Caso padrão: verifica se é reversão de PIX
    const description = transaction?.pixInfo?.isReversed
      ? translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.PIX_REVERSAL_DESCRIPTION'
        )
      : translate('COMPONENTS.USERS_TRANSACTIONS.DETAILS.DESCRIPTION');

    return {
      title: translate('COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE'),
      description,
      showWarningIcon: false,
    };
  }, [isInDecisionDeskAnalysis, transaction?.pixInfo?.isReversed]);

  const getStatusIcon = () => {
    if (!transaction) {
      return null;
    }

    if (
      transaction.status === TransactionStatus.SCHEDULE ||
      (transaction.transactionScheduleAt && !transaction.transactionAt)
    ) {
      return <PaymentStatusIcon status="scheduled" size={40} />;
    }

    if (transaction.status === TransactionStatus.EXECUTED) {
      return <PaymentStatusIcon status="completed" size={40} />;
    }

    return null;
  };

  const statusIcon = getStatusIcon();

  const fetchTransactionDetail = () => {
    if (params?.id === 'state') {
      return;
    }
    if (me && me.account.paymentAccounts.length > 0) {
      let cashbackAccount: models.PaymentAccount | undefined;

      if (navigate?.location?.state?.isCashback) {
        cashbackAccount = me.account.paymentAccounts.find(
          (account) => account.type === PaymentAccountType.CASHBACK_ACCOUNT
        );
      } else if (navigate?.location?.state?.isPayable) {
        cashbackAccount = me.account.paymentAccounts.find(
          (account) => account.type === PaymentAccountType.ACCOUNT
        );
      }

      if (!getStateValue('isScheduled')) {
        dispatch(
          DetailsAction.getTransactionDetails(
            params.id,
            cashbackAccount?.id ?? null
          )
        );
      } else {
        dispatch(DetailsAction.getTransactionDetailsScheduled(params.id));
      }
    }
  };

  const goToHome = () => {
    navigate(getStackPath('HOME'));
  };

  useEffect(() => {
    setRetry(1);

    if (params?.id !== 'state') {
      fetchTransactionDetail();

      return () => {
        setRetry(0);
        dispatch(DetailsAction.unsetDetails());
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, params.id]);

  useEffect(() => {
    if (!transaction?.amount && retry !== 3 && retry !== 0 && retry < 3) {
      dispatch(increaseDetailsLoading());
      setRetry((retry + 1) as typeof retry);
      setTimeout(() => {
        fetchTransactionDetail();
        dispatch(decreaseDetailsLoading());
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction]);

  useEffect(() => {
    setIsLoadingAttempt(true);
    if (
      attempts < 6 &&
      (transaction?.status == TransactionStatus.PENDING_CORE_BANK ||
        transaction?.status === TransactionStatus.PENDING_CONFIRM)
    ) {
      intervalRef.current = setInterval(() => {
        fetchTransactionDetail();
        setAttempts((prev) => prev + 1);
      }, 5000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempts, transaction?.status]);

  useEffect(() => {
    if (
      (transaction?.status !== TransactionStatus.PENDING_CORE_BANK &&
        transaction?.status !== TransactionStatus.PENDING_CONFIRM) ||
      attempts >= 6
    ) {
      setIsLoadingAttempt(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [transaction?.status, attempts]);

  const isFromHome =
    !getStateValue('isReceivable') &&
    !getStateValue('isExtract') &&
    !getStateValue('isEscrow') &&
    !getStateValue('isPayable');

  const getTitleBreadcrumb = () => {
    if (navigate?.location?.state?.isExtract) {
      return translate('COMPONENTS.BREADCRUMB.EXTRACT');
    }
    if (navigate?.location?.state?.isEscrow) {
      return translate('COMPONENTS.BREADCRUMB.ESCROW');
    }
    if (navigate?.location?.state?.isPayable) {
      return translate('COMPONENTS.BREADCRUMB.PAYMENT');
    }
    if (navigate?.location?.state?.isReceivable) {
      return translate('COMPONENTS.BREADCRUMB.RECEIVABLE');
    }
    return translate('COMPONENTS.BREADCRUMB.HOME');
  };

  const getUrlBreadcrumb = () => {
    if (navigate?.location?.state?.isExtract) {
      return `${getRouteStackPath('EXTRACT', 'EXTRACT_PAGE')}`;
    } else if (navigate?.location?.state?.isEscrow) {
      return `${getRouteStackPath('ESCROW', 'ESCROW_PAGE')}`;
    } else if (navigate?.location?.state?.isPayable) {
      return `${getRouteStackPath('PAYABLE', 'ACCOUNT_PAYABLE')}`;
    } else if (navigate?.location?.state?.isReceivable) {
      return `${getRouteStackPath('RECEIVABLE', 'ACCOUNT_RECEIVABLE')}`;
    } else {
      return `${getRouteStackPath('HOME', 'HOME_PAGE')}`;
    }
  };
  const canSendPixReversal =
    transaction?.flow === TransactionFlow.CASH_IN &&
    !transaction.isReversed &&
    transaction.pixInfo?.availableRefundAmount &&
    transaction.pixInfo.availableRefundAmount > 0 &&
    enablePixRefund;

  if (transaction?.status === TransactionStatus.REPROVED) {
    return <ErrorComponent />;
  }

  if (isLoadingAttempt) {
    return (
      <div className="transactions-details">
        <Row>
          <Col>
            <PanelContentBreadcrumb
              items={[
                {
                  active: true,
                  title: getTitleBreadcrumb(),
                  url: getUrlBreadcrumb(),
                },
                {
                  active: false,
                  title: translate(
                    'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE'
                  ),
                },
              ]}
            />
          </Col>
        </Row>
        <TransactionDetailsSkeleton />
      </div>
    );
  }

  if (
    hasAccess(roles, RolesType.TRANSACTION_READ) &&
    attempts > 5 &&
    (transaction?.status == TransactionStatus.PENDING_CORE_BANK ||
      transaction?.status === TransactionStatus.PENDING_CONFIRM)
  ) {
    return (
      <Modal
        open
        centered
        closable={false}
        footer={
          <div className="extract-page__button">
            <AdvancedButton
              text={translate(
                'COMPONENTS.USERS_TRANSACTIONS.DETAILS.GO_TO_STATEMENT'
              )}
              onClick={() => {
                NavigationService.navigate('/extrato');
              }}
            />
          </div>
        }
      >
        <div className="transactions-details__center_container">
          <p className="transactions-details__center_title">
            {translate(
              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE_PENDING_TRANSACTION'
            )}
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: getTitleBreadcrumb(),
            url: getUrlBreadcrumb(),
          },
          {
            active: false,
            title: translate('COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE'),
          },
        ]}
      />
      <div className="transactions-details">
        {!detailsLoading && retry === 3 ? (
          <div className="transactions-details__content">
            <div className="transactions-details__header-section">
              <PanelContentHeader
                pageTitle={translate(
                  `COMPONENTS.USERS_TRANSACTIONS.DETAILS.${
                    isFromHome ? 'TRY_AGAIN_TITLE' : 'ERROR_TITLE'
                  }`
                )}
                pageDescription={translate(
                  `COMPONENTS.USERS_TRANSACTIONS.DETAILS.${
                    isFromHome ? 'TRY_AGAIN_DESCRIPTION' : 'ERROR_DESCRIPTION'
                  }`
                )}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="transactions-details__content">
              <div className="transactions-details__header-section">
                <div className="transactions-details__title-container">
                  {!detailsLoading ? (
                    <>
                      {transaction?.status === TransactionStatus.SCHEDULE ||
                      (transaction?.transactionScheduleAt &&
                        !transaction?.transactionAt) ? (
                        statusIcon ? (
                          <div className="transactions-details__title-with-icon">
                            <div className="transactions-details__title-with-icon__icon">
                              {statusIcon}
                            </div>
                            <div className="transactions-details__title-with-icon__content">
                              <PanelContentHeader
                                pageTitle={translate(
                                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE_SCHEDULED'
                                )}
                                pageDescription={translate(
                                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.SCHEDULE_DESCRIPTION'
                                )}
                              />
                            </div>
                          </div>
                        ) : (
                          <PanelContentHeader
                            pageTitle={translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE_SCHEDULED'
                            )}
                            pageDescription={translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.SCHEDULE_DESCRIPTION'
                            )}
                          />
                        )
                      ) : null}

                      {(transaction?.status ===
                        TransactionStatus.PENDING_CORE_BANK ||
                        transaction?.status ===
                          TransactionStatus.PENDING_CONFIRM) &&
                        !transaction?.transactionScheduleAt &&
                        (statusIcon ? (
                          <div className="transactions-details__title-with-icon">
                            <div className="transactions-details__title-with-icon__icon">
                              {statusIcon}
                            </div>
                            <div className="transactions-details__title-with-icon__content">
                              <PanelContentHeader
                                pageTitle={translate(
                                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE_REQUESTED'
                                )}
                                pageDescription={translate(
                                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.REQUESTED_DESCRIPTION'
                                )}
                              />
                            </div>
                          </div>
                        ) : (
                          <PanelContentHeader
                            pageTitle={translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE_REQUESTED'
                            )}
                            pageDescription={translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.REQUESTED_DESCRIPTION'
                            )}
                          />
                        ))}

                      {transaction?.status === TransactionStatus.EXECUTED &&
                        !transaction?.transactionScheduleAt &&
                        (statusIcon ? (
                          <div className="transactions-details__title-with-icon">
                            <div className="transactions-details__title-with-icon__icon">
                              {statusIcon}
                            </div>
                            <div className="transactions-details__title-with-icon__content">
                              <PanelContentHeader
                                pageTitle={translate(
                                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE'
                                )}
                                pageDescription={titleConfig.description}
                              />
                            </div>
                          </div>
                        ) : (
                          <PanelContentHeader
                            pageTitle={translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE'
                            )}
                            pageDescription={titleConfig.description}
                          />
                        ))}

                      {isInDecisionDeskAnalysis ? (
                        <div className="transactions-details__title-with-icon">
                          <div className="transactions-details__title-with-icon__icon">
                            <WarningOutlined
                              style={{
                                fontSize: 40,
                                color: '#ffc107',
                              }}
                            />
                          </div>
                          <div className="transactions-details__title-with-icon__content">
                            <PanelContentHeader
                              pageTitle={titleConfig.title}
                              pageDescription={titleConfig.description}
                            />
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="transactions-details__header-skeleton">
                      <div className="skeleton-title" />
                      <div className="skeleton-description" />
                    </div>
                  )}
                </div>

                <div className="transactions-details__actions-container">
                  {!detailsLoading ? (
                    <>
                      {!isInDecisionDeskAnalysis && (
                        <AdvancedButton
                          className="transactions-details__share-button"
                          text={translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.BUTTONS.SHARE'
                          )}
                          onClick={() => {
                            setVisible(true);
                          }}
                        />
                      )}
                      {hasAccess(roles, RolesType.MY_ACCOUNT) &&
                        transaction?.type === TransactionType.PIX &&
                        !!canSendPixReversal && (
                          <AdvancedButton
                            className="transactions-details__reversal-button"
                            text={translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.BUTTONS.REVERSAL'
                            )}
                            onClick={() => setPixReversalModal(true)}
                          />
                        )}
                      {hasAccess(roles, RolesType.MY_ACCOUNT) &&
                      transaction?.transactionScheduleAt &&
                      !transaction.transactionAt &&
                      retry !== 3 ? (
                        <AdvancedButton
                          className="transactions-details__cancel-button"
                          text={translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.BUTTONS.CANCEL'
                          )}
                          onClick={() => setIsCancelPinModalVisible(true)}
                        />
                      ) : null}
                      <AdvancedButton
                        className="transactions-details__share-button"
                        text={translate(
                          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.BUTTONS.GO_BACK'
                        )}
                        onClick={() =>
                          navigate(getRouteStackPath('EXTRACT', 'EXTRACT_PAGE'))
                        }
                      />
                    </>
                  ) : (
                    <div className="transactions-details__actions-loading">
                      <div className="skeleton-button" />
                      <div className="skeleton-button" />
                    </div>
                  )}
                </div>
              </div>

              {isInDecisionDeskAnalysis ? (
                <div className="transactions-details__decision-desk-alert">
                  <div className="transactions-details__decision-desk-alert__icon">
                    <WarningOutlined />
                  </div>
                  <div className="transactions-details__decision-desk-alert__content">
                    <p className="transactions-details__decision-desk-alert__content__title">
                      {translate(
                        'COMPONENTS.USERS_TRANSACTIONS.DETAILS.DECISION_DESK_ANALYSIS_TITLE'
                      )}
                    </p>
                    <p className="transactions-details__decision-desk-alert__content__description">
                      {translate(
                        'COMPONENTS.USERS_TRANSACTIONS.DETAILS.DECISION_DESK_ANALYSIS_DESCRIPTION'
                      )}
                    </p>
                  </div>
                </div>
              ) : null}

              <div className="transactions-details__main-content">
                {!detailsLoading ? (
                  <TransactionDetailsComponent
                    transaction={transaction}
                    username={me.account.name ?? ''}
                  />
                ) : (
                  <TransactionDetailsSkeleton />
                )}
              </div>
            </div>
          </>
        )}

        {visible ? (
          <StatementProofModal
            visible={visible}
            onClose={() => setVisible(false)}
            transaction={transaction}
            username={me.account.name ?? ''}
            hideEmail={
              transaction?.paymentAccountType ===
              PaymentAccountType.ACCOUNTSECONDARY
            }
            statementProofType={StatementProofType.TRANSACTION}
            title={translate(
              `COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE${
                transaction?.transactionScheduleAt ? '_SCHEDULED' : ''
              }`
            )}
          />
        ) : null}

        {hasAccess(roles, RolesType.MY_ACCOUNT) &&
          transaction?.type === TransactionType.PIX && (
            <>
              <PixReversalModal
                visible={pixReversalModal}
                setVisible={setPixReversalModal}
                reason={reason}
                onChange={setReason}
                onConfirmation={() => setIsReversalPinModalVisible(true)}
                setAmount={setAmount}
                amount={amount}
                setRawAmount={setRawAmount}
              />
              <ConfirmationModal
                visible={isReversalPinModalVisible}
                setVisible={(value) => setIsReversalPinModalVisible(value)}
                handleConfirmation={handleConfirmation}
                text={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TEXT')}
                title={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TITLE')}
              />
            </>
          )}

        {hasAccess(roles, RolesType.MY_ACCOUNT) &&
        transaction?.transactionScheduleAt &&
        !transaction.transactionAt &&
        !detailsLoading &&
        retry !== 3 ? (
          <ConfirmationModal
            visible={isCancelPinModalVisible}
            setVisible={setIsCancelPinModalVisible}
            handleConfirmation={handleCancelScheduledPix}
            text={translate('PAGES.HOME.TRANSFER.PIN_CANCEL_TEXT')}
            title={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TITLE')}
          />
        ) : null}

        {!detailsLoading && retry === 3 && (
          <div className="transactions-details__error-actions">
            <AdvancedButton
              className="transactions-details__leave-button"
              text={translate(
                'COMPONENTS.USERS_TRANSACTIONS.DETAILS.BUTTONS.LEAVE'
              )}
              onClick={() => goToHome()}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default TransactionDetails;
